import { useFormik } from "formik";
import * as Yup from "yup";
import { Card, Form, Button } from "react-bootstrap";
import { useUserDetail } from "../core/QueryResponseProvider";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

const DetailUser = () => {
  const navigate = useNavigate();
  const { response } = useUserDetail();
  const { detailData } = response ?? {};

  const formik = useFormik({
    enableReinitialize: true, // penting biar initialValues update saat detailData masuk
    initialValues: {
      fullName: detailData?.fullName || "",
      userName: detailData?.userName || "",
      email: detailData?.email || "",
      merchantId: detailData?.merchantId || "",
      role: detailData?.role || "",
      tokenLogin: detailData?.tokenLogin || "",
      tokenApproval: detailData?.tokenApproval || "",
      isActive: detailData?.isActive ?? true,
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Full Name is required"),
      userName: Yup.string().required("Username is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      role: Yup.string().required("Role is required"),
      tokenLogin: Yup.string().required("Token Login is required"),
      tokenApproval: Yup.string().required("Token Approval is required"),
    }),
    onSubmit: (values) => {
      console.log("Edited User:", values);
      // TODO: call API update user
    },
  });

  // Helper function to get role display name
  const getRoleDisplayName = (role: string) => {
    const roleMap: { [key: string]: string } = {
      'INTERNAL_ADMIN': 'Internal Admin',
      'INTERNAL_MAKER': 'Internal Maker',
      'INTERNAL_APPROVAL': 'Internal Approval',
      'MERCHANT_ADMIN': 'Merchant Admin',
      'MERCHANT_MAKER': 'Merchant Maker',
      'MERCHANT_APPROVAL': 'Merchant Approval'
    };
    return roleMap[role] || role;
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header Section */}
      <div className="container-fluid px-8 mb-6">
        <div className="bg-white rounded-4 shadow-sm p-6 border-0">
          <div className="d-flex align-items-center justify-content-between">
            <div className="mb-4">
              <h3 className="fw-bold text-gray-800 mb-2">User Details</h3>
              <p className="text-muted fs-7 mb-0">View comprehensive user information and account details</p>
            </div>
            <div className="d-flex align-items-center gap-3">
              {formik.values.isActive ? (
                <span className="badge rounded-pill bg-success">Active</span>
              ) : (
                <span className="badge rounded-pill bg-secondary">Inactive</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-fluid px-8 flex-grow-1">
        <div className="row g-6">
          {/* User Information Card */}
          <div className="col-12">
            <div className="card shadow-sm border-0 rounded-4">
              <div className="card-header bg-white py-4" style={{borderBottom: '2px solid #e4e6ef'}}>
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-35px me-3">
                    <div className="symbol-label bg-primary">
                      <i className="fas fa-user text-white"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="card-title mb-0 fw-bold text-gray-800">User Information</h3>
                    <p className="card-subtitle text-muted fs-6 mb-0">Basic user details and account information</p>
                  </div>
                </div>
              </div>
              <div className="card-body p-10">
                <div className="row g-6">
                  <div className="col-md-6">
                    <div className="mb-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">Full Name</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-user text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={formik.values.fullName}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">Username</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-at text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={formik.values.userName}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">Email Address</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-envelope text-gray-600"></i>
                        </span>
                        <input
                          type="email"
                          className="form-control bg-light"
                          value={formik.values.email}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">User Role</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-shield-alt text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={getRoleDisplayName(formik.values.role)}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {formik.values.merchantId && (
                    <div className="col-12">
                      <div className="mb-6">
                        <label className="form-label text-gray-700 fw-semibold mb-3">Assigned Merchant</label>
                        <div className="input-group input-group-lg">
                          <span className="input-group-text bg-light">
                            <i className="fas fa-building text-gray-600"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={formik.values.merchantId}
                            disabled
                            readOnly
                          />
                        </div>
                        <div className="form-text text-muted mt-2">
                          <i className="fas fa-info-circle me-1"></i>
                          Merchant ID assigned to this user account
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Security Information Card */}
          <div className="col-12">
            <div className="card shadow-sm border-0 rounded-4">
              <div className="card-header bg-white py-4" style={{borderBottom: '2px solid #e4e6ef'}}>
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-35px me-3">
                    <div className="symbol-label bg-primary">
                      <i className="fas fa-key text-white"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="card-title mb-0 fw-bold text-gray-800">Security Information</h3>
                    <p className="card-subtitle text-muted fs-6 mb-0">Authentication tokens and security credentials</p>
                  </div>
                </div>
              </div>
              <div className="card-body p-10">
                <div className="row g-6">
                  <div className="col-md-6">
                    <div className="mb-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">Login Token</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-fingerprint text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control bg-light font-monospace"
                          value={formik.values.tokenLogin || 'Not configured'}
                          disabled
                          readOnly
                        />
                      </div>
                      <div className="form-text text-muted mt-2">
                        <i className="fas fa-info-circle me-1"></i>
                        Token used for user authentication and login verification
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">Approval Token</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-shield-check text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control bg-light font-monospace"
                          value={formik.values.tokenApproval || 'Not configured'}
                          disabled
                          readOnly
                        />
                      </div>
                      <div className="form-text text-muted mt-2">
                        <i className="fas fa-info-circle me-1"></i>
                        Token required for transaction approvals and sensitive operations
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-4 shadow-sm border-0 p-6 mt-6">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted">
              <div className="fs-7">User account information is read-only</div>
              <div className="fs-8">Use the edit function to modify user details</div>
            </div>
            <div className="d-flex gap-3">
              <button
                type="button"
                className="btn btn-light btn-lg px-8"
                onClick={() => navigate(-1)}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to List
              </button>
              <button
                type="button"
                className="btn btn-primary btn-lg px-8 shadow-primary"
                onClick={() => navigate(`/user-management/edit/${detailData?._id}`)}
              >
                <i className="fas fa-edit me-2"></i>
                Edit User
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailUser;
